import * as React from 'react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import Paper from '@mui/material/Paper';
import cn from 'classnames';
import MediaQuery, { useMediaQuery } from 'react-responsive';
import { useTranslation } from 'react-i18next';
import { InfoMessagePositionsEnum } from '@websky/graphql';
import { default as BaseButton } from '../../../BaseComponents/Button';
import { useDesktopFlightModalContent, useDesktopFlightModalMobileTabs, useLastSelectedFlight } from './hooks';
import { Tabs } from '../../types';
import { useTheme } from '../../../theme';
import { FlightInfoV2 } from '../../../FlightInfo';
import { usePagination } from '@shared/ui';
import { MOBILE_MIN_WIDTH, TABLET_MIN_WIDTH } from '../../../utils';
import { PaginationWithOverride, Tabs as MobileTabs } from '@shared/ui';
import CompareFares from '../../../CompareFares/components/CompareFares/CompareFares';
import Link from '../../../Link';
import { useRenderers } from '../../../renderProps';
import { findIndexFirstAvailableFareGroup, isFareGroupAvailable, isFaresHasOneItem, isShowFaresPagination, roundAvailableSliderIndex } from './utils';
import FlightInfoSegment from '../FlightInfoSegment/FlightInfoSegment';
import { getDurationString, getFlightDuration } from '../../../Results/utils';
import { getCheapestFlightInCombination, getNextSegment } from '../../utils';
import { analytics_fareAddToCart, analytics_setFareGroupsListItems } from '../../../Results/components/Results/dataLayer/results';
import InfoMessages from '../../../InfoMessages';
import { filterMessageByPosition } from '../../../InfoMessages/utils';
import { SwipeableDrawer } from '@mui/material';
import { useToggleable } from '../../../hooks';
import ModalX from '../../../BaseComponents/ModalX';
import SlickSlider from '../../../SlickSlider';
import { CdnIcon } from '@shared/ui';
import { useGetFfpBonusInfoById } from '@features/fares';
import { FareGroup as FareGroupDefault } from '@features/fares';
const DesktopFlightModalContent = props => {
    var _a, _b;
    const { isSelectorInModal = true, enableScrollIntoViewFareButton = true } = props;
    const { active, handleBusinessButtonClick, handleEconomyButtonCLick, handleMilesButtonClick, isFareCompareOpen, openFareCompare, closeFareCompare } = useDesktopFlightModalContent(props);
    const { t } = useTranslation('FlightInfoModalContent');
    const { FareGroup = FareGroupDefault, DesktopFlightContentFlightInfo: DesktopFlightContentFlightInfoOverride = FlightInfoV2 } = useRenderers();
    const isTablet = useMediaQuery({ maxWidth: TABLET_MIN_WIDTH });
    const isMobile = useMediaQuery({ maxWidth: MOBILE_MIN_WIDTH });
    const isSmallMobile = useMediaQuery({ maxWidth: 380 });
    const isSelectorInModalOrMobile = isSelectorInModal || isMobile;
    const allFares = useMemo(() => {
        return !isSelectorInModalOrMobile ? [...props.economyFares, ...props.businessFares, ...props.milesFares] : [];
    }, [isSelectorInModalOrMobile, props.economyFares, props.businessFares, props.milesFares]);
    const lastSelectedFare = useLastSelectedFlight();
    const isActiveEconomyFares = active === Tabs.EconomyFares;
    const isActiveBusinessFares = active === Tabs.BusinessFares;
    const isActiveMilesFares = active === Tabs.MilesFares;
    const isActiveFlightInfo = active === Tabs.FlightInfo;
    const isOneEconomyFare = isFaresHasOneItem(props.economyFares);
    const isOneBusinessFare = isFaresHasOneItem(props.businessFares);
    const isOneMilesFare = isFaresHasOneItem(props.milesFares);
    const isOneAllFare = isFaresHasOneItem(allFares);
    const slidesShowMobile = isSmallMobile ? 1 : isMobile ? 1.2 : 2;
    const slidesToShow = isTablet ? slidesShowMobile : (_b = (_a = props.sliderSettings) === null || _a === void 0 ? void 0 : _a.slidesToShow) !== null && _b !== void 0 ? _b : 3;
    const slidesToScroll = slidesToShow >= 2 ? slidesToShow : 1;
    const slidesToShowOnce = props.slidesToShowOnce || 1.1;
    const { isOpen, open, close, toggle } = useToggleable(false);
    const { firstEconomyAvailableSliderIndex, firstBusinessAvailableSliderIndex } = useMemo(() => {
        const firstEconomyAvailableFareGroup = findIndexFirstAvailableFareGroup(props.economyFares, lastSelectedFare);
        const firstBusinessAvailableFareGroup = findIndexFirstAvailableFareGroup(props.businessFares, lastSelectedFare);
        const firstEconomyAvailableSliderIndex = roundAvailableSliderIndex(firstEconomyAvailableFareGroup, slidesToScroll);
        const firstBusinessAvailableSliderIndex = roundAvailableSliderIndex(firstBusinessAvailableFareGroup, slidesToScroll);
        return {
            firstEconomyAvailableSliderIndex,
            firstBusinessAvailableSliderIndex
        };
    }, [props.economyFares, props.businessFares, lastSelectedFare, slidesToScroll]);
    const { currentIndex: economySliderIndex, handleChange: handleEconomySliderIndexChange } = usePagination(firstEconomyAvailableSliderIndex);
    const { currentIndex: businessSliderIndex, handleChange: handleBusinessSliderIndexChange } = usePagination(firstBusinessAvailableSliderIndex);
    const { currentIndex: milesSliderIndex, handleChange: handleMilesSliderIndexChange } = usePagination(0);
    const { currentIndex: allFaresSliderIndex, handleChange: handleAllFaresSliderIndexChange } = usePagination(0);
    const economySliderRef = React.useRef();
    const businessSliderRef = React.useRef();
    const milesSliderRef = React.useRef();
    useEffect(() => {
        // return to previous item when user change active tab
        handleEconomySliderIndexChange(firstEconomyAvailableSliderIndex);
        handleBusinessSliderIndexChange(firstBusinessAvailableSliderIndex);
    }, [active]);
    useEffect(() => {
        var _a;
        (_a = props.setIsComparisonOpen) === null || _a === void 0 ? void 0 : _a.call(props, isFareCompareOpen);
    }, [isFareCompareOpen]);
    const { ModalContent: theme } = useTheme('DesktopFlight');
    const { flightIdsToRequestMiles, segmentsForFlightToRequestMiles } = useMemo(() => {
        var _a, _b;
        /** Тарифы по активному табу */
        const faresForActiveTab = (() => {
            switch (active) {
                case Tabs.EconomyFares:
                    return props.economyFares;
                case Tabs.BusinessFares:
                    return props.businessFares;
                case Tabs.MilesFares:
                    return props.milesFares;
                default:
                    return [];
            }
        })();
        /** Если есть allFares — используем все тарифы */
        const fares = (allFares === null || allFares === void 0 ? void 0 : allFares.length) ? allFares : faresForActiveTab;
        const flightIdsToRequestMiles = fares
            .map(fare => {
            var _a;
            const cheapestFlight = getCheapestFlightInCombination(fare, props.selectedFares);
            return (_a = cheapestFlight === null || cheapestFlight === void 0 ? void 0 : cheapestFlight.flight) === null || _a === void 0 ? void 0 : _a.id;
        })
            .filter(Boolean);
        const segmentsForFlightToRequestMiles = (_b = (_a = props.flight) === null || _a === void 0 ? void 0 : _a.segments) === null || _b === void 0 ? void 0 : _b.map(segment => segment === null || segment === void 0 ? void 0 : segment.segment);
        return { flightIdsToRequestMiles, segmentsForFlightToRequestMiles };
    }, [
        active,
        allFares,
        props.flight,
        props.milesFares,
        props.economyFares,
        props.businessFares,
        props.selectedFares
    ]);
    const { ffpBonusInfoById } = useGetFfpBonusInfoById({
        legId: props.legId,
        flightIds: flightIdsToRequestMiles,
        segments: segmentsForFlightToRequestMiles,
        needToMakeRequest: props.isFaresMilesMustBeRequested
    });
    const renderFareGroup = (group, index, cheapestFlight, disabledForPriceInMiles, disabled, dataTestId) => {
        var _a;
        const onFareSelect = () => {
            analytics_fareAddToCart(group, index, props.flight, props.searchParameters);
            props.getOnFareSelect(group)();
        };
        return (React.createElement(FareGroup, { key: index, className: cn(theme.fareGroup__slide, {
                [theme.fareGroup_withoutUpgradeOption]: !!props.lastSelectedFare
            }), fare: group.fareFamily, price: !props.hidePrice && (cheapestFlight === null || cheapestFlight === void 0 ? void 0 : cheapestFlight.price) ? cheapestFlight.price : null, originalPrice: !props.hidePrice && (cheapestFlight === null || cheapestFlight === void 0 ? void 0 : cheapestFlight.originalPrice) ? cheapestFlight.originalPrice : null, priceInMiles: cheapestFlight === null || cheapestFlight === void 0 ? void 0 : cheapestFlight.priceInMiles, ffpBonusInfo: ffpBonusInfoById[(_a = cheapestFlight === null || cheapestFlight === void 0 ? void 0 : cheapestFlight.flight) === null || _a === void 0 ? void 0 : _a.id], upgradeOption: props.upgradeOptions.get(group.fareFamily.id), disabledForMiles: disabledForPriceInMiles, dataTestId: dataTestId, disabled: disabled, onSelect: onFareSelect, onUpgrade: props.upgradeOptions.has(group.fareFamily.id) &&
                props.getOnFareSelect(props.upgradeOptions.get(group.fareFamily.id).canUpgradeTo), showUnavailableOptions: true }));
    };
    const renderFareGroups = (fareGroups) => {
        let availableFareGroupIndex = 0;
        return fareGroups.map((fareGroup, index) => {
            var _a;
            const isAvailable = isFareGroupAvailable(fareGroup, lastSelectedFare);
            const cheapestFlight = getCheapestFlightInCombination(fareGroup, props.selectedFares);
            const disabledForPriceInMiles = (cheapestFlight === null || cheapestFlight === void 0 ? void 0 : cheapestFlight.priceInMiles) > 0 && !((_a = cheapestFlight === null || cheapestFlight === void 0 ? void 0 : cheapestFlight.flight) === null || _a === void 0 ? void 0 : _a.canBePaidByMiles);
            const disabled = !isAvailable || disabledForPriceInMiles;
            const dataTestId = disabled ? undefined : (++availableFareGroupIndex).toString();
            return renderFareGroup(fareGroup, index, cheapestFlight, disabledForPriceInMiles, disabled, dataTestId);
        });
    };
    const renderSliderIndex = (slidesCount, index, slidesToScroll) => {
        let pagesCount = Math.ceil(slidesCount / slidesToScroll);
        if (isMobile) {
            pagesCount = slidesCount;
        }
        if ((isTablet && slidesCount > 2) || slidesCount > sliderSettings.slidesToScroll) {
            return (React.createElement("span", { className: theme.pagination__index },
                Math.ceil(index / slidesToScroll) + 1,
                " / ",
                pagesCount));
        }
        else {
            return null;
        }
    };
    const isEconomyExists = React.useMemo(() => {
        return props.economyFares.some(economyFare => economyFare.prices);
    }, [props.economyFares]);
    const isBusinessFaresExists = React.useMemo(() => {
        return props.businessFares.some(businessFare => businessFare.prices);
    }, [props.businessFares]);
    const isMilesExists = React.useMemo(() => {
        return props.milesFares.some(milesFare => milesFare.prices);
    }, [props.milesFares]);
    const container = useRef();
    const sliderAnchorRef = useRef();
    const onScrollTop = useCallback(() => {
        container.current.scrollIntoView({
            block: 'start',
            behavior: 'smooth'
        });
    }, [container]);
    useEffect(() => {
        analytics_setFareGroupsListItems([...props.economyFares, ...props.businessFares, ...props.milesFares], props.flight, props.searchParameters);
    }, []);
    useEffect(() => {
        var _a;
        if (enableScrollIntoViewFareButton) {
            (_a = sliderAnchorRef === null || sliderAnchorRef === void 0 ? void 0 : sliderAnchorRef.current) === null || _a === void 0 ? void 0 : _a.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
    }, [enableScrollIntoViewFareButton]);
    const renderInfoMessages = (position, infoMessages) => {
        var _a, _b, _c;
        const messages = (_c = (_b = (_a = props.flight.segments[0]) === null || _a === void 0 ? void 0 : _a.segment) === null || _b === void 0 ? void 0 : _b.messages) === null || _c === void 0 ? void 0 : _c.filter(message => filterMessageByPosition(position, message));
        // to prevent the slide from being displayed if there are no messages
        if (messages === null || messages === void 0 ? void 0 : messages.length) {
            return infoMessages;
        }
        return null;
    };
    const infoMessageByClass = React.useMemo(() => {
        const position = active === Tabs.EconomyFares
            ? InfoMessagePositionsEnum.upsaleEconomy
            : InfoMessagePositionsEnum.upsaleBusiness;
        return renderInfoMessages(position, React.createElement(InfoMessages, { position: position, className: theme.infoMessage, notificationsClassName: theme.infoMessage__notifications, itemHtmlClassName: theme.infoMessage__htmlMessage, htmlClassName: theme.infoMessage__htmlContainer, messages: props.flight.segments[0].segment.messages }));
    }, [props.flight.segments, active]);
    const infoMessageRight = React.useMemo(() => {
        const position = InfoMessagePositionsEnum.upsaleRight;
        return renderInfoMessages(position, React.createElement(InfoMessages, { position: position, className: cn(theme.infoMessage, theme.upsaleRightInfoMessage), notificationsClassName: theme.infoMessage__notifications, itemHtmlClassName: theme.infoMessage__htmlMessage, contentClassName: theme.infoMessage__contentClassName, messages: props.flight.segments[0].segment.messages }));
    }, [props.flight.segments]);
    const hasInfoMessage = !!infoMessageByClass || !!infoMessageRight;
    const isOneActiveFare = (isActiveEconomyFares && isOneEconomyFare && !hasInfoMessage) ||
        (isActiveBusinessFares && isOneBusinessFare && !hasInfoMessage) ||
        (isActiveMilesFares && isOneMilesFare && !hasInfoMessage);
    const sliderSettings = Object.assign(Object.assign({ slidesToShow: isOneActiveFare ? slidesToShowOnce : slidesToShow, infinite: false, speed: 250, slidesToScroll, swipe: isTablet }, props.sliderSettings), { className: theme.slider });
    /**
     * У beforeChange в nextSlide может приходить дробное значение.
     * Округляем, чтобы корректно отображалась пагинация.
     */
    const handleBeforeChange = (callback) => (_, nextSlide) => callback(Math.round(nextSlide));
    const allSliderSettings = Object.assign(Object.assign({}, sliderSettings), { slidesToShow: isOneAllFare ? slidesToShowOnce : slidesToShow, className: cn(sliderSettings.className, { [theme.slider_allOnce]: isOneAllFare }), beforeChange: handleBeforeChange(handleAllFaresSliderIndexChange) });
    const economySliderSettings = Object.assign(Object.assign({}, sliderSettings), { className: cn(sliderSettings.className, theme.slider_economy), initialSlide: firstEconomyAvailableSliderIndex, beforeChange: handleBeforeChange(handleEconomySliderIndexChange) });
    const businessSliderSettings = Object.assign(Object.assign({}, sliderSettings), { className: cn(sliderSettings.className, theme.slider_business), initialSlide: firstBusinessAvailableSliderIndex, beforeChange: handleBeforeChange(handleBusinessSliderIndexChange) });
    const milesSliderSettings = Object.assign(Object.assign({}, sliderSettings), { className: cn(sliderSettings.className, theme.slider_miles), beforeChange: handleBeforeChange(handleMilesSliderIndexChange) });
    const sliderWrapperClassName = cn(theme.slider__wrp, {
        [theme.slider__wrp_singleItem]: isOneActiveFare && !hasInfoMessage
    });
    const { tabs: mobileFareGroupTabs, active: activeFareGroupMobileTab, onChange: onChangeFareGroupMobileTabs } = useDesktopFlightModalMobileTabs({
        isEconomyExists,
        isMilesExists,
        isBusinessFaresExists,
        isActiveEconomyFares,
        isActiveBusinessFares,
        isActiveMilesFares,
        handleEconomyButtonCLick,
        handleBusinessButtonClick,
        handleMilesButtonClick,
        economyFares: props.economyFares,
        businessFares: props.businessFares,
        milesFares: props.milesFares
    });
    return (React.createElement(Paper, { elevation: 0, className: cn(theme.paper, {
            [theme.paper_dropdown]: !isSelectorInModalOrMobile,
            [theme.paper_pagination]: !isSelectorInModalOrMobile && allFares.length > allSliderSettings.slidesToScroll
        }), ref: container },
        React.createElement(InfoMessages, { position: InfoMessagePositionsEnum.upsaleTop, className: theme.topInfoMessage, itemHtmlClassName: theme.topInfoMessage__htmlMessage, messages: props.flight.segments[0].segment.messages }),
        React.createElement(MediaQuery, { minWidth: MOBILE_MIN_WIDTH },
            React.createElement("div", { className: theme.buttons },
                isSelectorInModalOrMobile && (React.createElement(React.Fragment, null,
                    (isEconomyExists || (!isMilesExists && !isBusinessFaresExists)) && (React.createElement(BaseButton, { variant: "optionAction", className: cn(theme.fareGroup__button, {
                            [theme.fareGroup__button_active]: isActiveEconomyFares
                        }), onClick: handleEconomyButtonCLick }, t('Economy'))),
                    isBusinessFaresExists && (React.createElement(BaseButton, { variant: "optionAction", className: cn(theme.fareGroup__button, theme.fareGroup__button_business, {
                            [theme.fareGroup__button_active]: isActiveBusinessFares
                        }), onClick: handleBusinessButtonClick }, t('Business'))),
                    isMilesExists && (React.createElement(BaseButton, { variant: "optionAction", className: cn(theme.fareGroup__button, theme.fareGroup__button, {
                            [theme.fareGroup__button_active]: isActiveMilesFares
                        }), onClick: handleMilesButtonClick }, t('Premium'))))),
                active !== Tabs.FlightInfo && (React.createElement(Link, { className: theme.compareFaresLink, action: openFareCompare },
                    t('View full conditions'),
                    React.createElement(CdnIcon, { className: theme.compareFaresLink_icon, icon: "Info/Details" }))))),
        React.createElement(MediaQuery, { maxWidth: MOBILE_MIN_WIDTH },
            React.createElement(MobileTabs, { classes: {
                    tabs: theme.mobileTabs,
                    tab: theme.mobileTabs__tab,
                    description: theme.mobileTabs__description
                }, tabs: mobileFareGroupTabs, active: activeFareGroupMobileTab, onChange: onChangeFareGroupMobileTabs })),
        React.createElement("div", { className: theme.content },
            React.createElement("div", { className: cn(theme.content_wrapper, {
                    [theme.content_offset]: hasInfoMessage
                }) },
                React.createElement(MediaQuery, { minWidth: MOBILE_MIN_WIDTH },
                    React.createElement("div", null, allFares.length > 0 ? (React.createElement("div", { className: sliderWrapperClassName },
                        React.createElement(SlickSlider, Object.assign({}, allSliderSettings), renderFareGroups(allFares)),
                        renderSliderIndex(allFares.length, allFaresSliderIndex, allSliderSettings.slidesToScroll))) : (React.createElement(React.Fragment, null,
                        isActiveEconomyFares && (React.createElement("div", { className: sliderWrapperClassName },
                            React.createElement(SlickSlider, Object.assign({}, economySliderSettings),
                                infoMessageByClass,
                                renderFareGroups(props.economyFares),
                                infoMessageRight),
                            renderSliderIndex(props.economyFares.length, economySliderIndex, slidesToScroll))),
                        isActiveBusinessFares && (React.createElement("div", { className: sliderWrapperClassName },
                            React.createElement(SlickSlider, Object.assign({}, businessSliderSettings),
                                infoMessageByClass,
                                renderFareGroups(props.businessFares),
                                infoMessageRight),
                            renderSliderIndex(props.businessFares.length, businessSliderIndex, slidesToScroll))),
                        isActiveMilesFares && (React.createElement("div", { className: sliderWrapperClassName },
                            React.createElement(SlickSlider, Object.assign({}, milesSliderSettings),
                                renderFareGroups(props.milesFares),
                                infoMessageRight),
                            renderSliderIndex(props.milesFares.length, milesSliderIndex, milesSliderSettings.slidesToShow)))))),
                    React.createElement("div", { ref: sliderAnchorRef }),
                    isActiveFlightInfo && (React.createElement(DesktopFlightContentFlightInfoOverride, { flight: props.flight, selectedFareFamily: props.lastSelectedFare }))),
                React.createElement(MediaQuery, { maxWidth: MOBILE_MIN_WIDTH },
                    isActiveEconomyFares && (React.createElement("div", { className: sliderWrapperClassName },
                        React.createElement(SlickSlider, Object.assign({}, economySliderSettings, { ref: economySliderRef }), renderFareGroups(props.economyFares)),
                        isShowFaresPagination(props.economyFares) && (React.createElement(PaginationWithOverride, { className: theme.pagination, variant: "bigCircles", itemsCount: props.economyFares.length, currentIndex: economySliderIndex, onChange: index => economySliderRef.current.slickGoTo(index) })))),
                    isActiveBusinessFares && (React.createElement("div", { className: sliderWrapperClassName },
                        React.createElement(SlickSlider, Object.assign({}, businessSliderSettings, { ref: businessSliderRef }), renderFareGroups(props.businessFares)),
                        isShowFaresPagination(props.businessFares) && (React.createElement(PaginationWithOverride, { className: theme.pagination, variant: "bigCircles", itemsCount: props.businessFares.length, currentIndex: businessSliderIndex, onChange: index => businessSliderRef.current.slickGoTo(index) })))),
                    isActiveMilesFares && (React.createElement("div", { className: sliderWrapperClassName },
                        React.createElement(SlickSlider, Object.assign({}, milesSliderSettings, { ref: milesSliderRef }), renderFareGroups(props.milesFares)),
                        isShowFaresPagination(props.milesFares) && (React.createElement(PaginationWithOverride, { className: theme.pagination, variant: "bigCircles", itemsCount: props.milesFares.length, currentIndex: milesSliderIndex, onChange: index => milesSliderRef.current.slickGoTo(index) })))),
                    React.createElement("div", { ref: sliderAnchorRef }),
                    active !== Tabs.FlightInfo && (React.createElement("div", { className: theme.compareFaresLink__mobile_wrapper },
                        React.createElement(Link, { className: theme.compareFaresLink__mobile, action: openFareCompare },
                            t('View full conditions'),
                            React.createElement(CdnIcon, { className: theme.compareFaresLink__mobile_icon, icon: "Arrow/Chevron_right" })))),
                    hasInfoMessage && (React.createElement(SwipeableDrawer, { anchor: "bottom", classes: { paper: theme.swipeable__paper, root: theme.swipeable__paperRoot }, open: isOpen, onOpen: open, onClose: close, onClick: toggle, swipeAreaWidth: 76, disableSwipeToOpen: false, ModalProps: { keepMounted: true } }, infoMessageRight !== null && infoMessageRight !== void 0 ? infoMessageRight : infoMessageByClass))),
                isSelectorInModalOrMobile && (React.createElement("div", { className: theme.segments },
                    props.flight.segments.map((segment, index) => (React.createElement(FlightInfoSegment, { key: index, className: theme.segments__flight, segment: segment, isMobile: isTablet, nextSegment: segment.transferDuration ? getNextSegment(props.flight.segments, index) : null, isReturnFlight: props.isReturnFlight }))),
                    props.flight.segments.length > 1 && (React.createElement("div", { className: theme.total_time },
                        t('Total duration'),
                        ": ",
                        getDurationString(getFlightDuration(props.flight.segments)))),
                    React.createElement("div", { className: theme.content_footer },
                        React.createElement(BaseButton, { variant: "smallPrimary", onClick: onScrollTop, className: theme.scroll_up }, t('Scroll up'))))))),
        React.createElement(ModalX, { isOpen: isFareCompareOpen, noPadding: true, size: "xlarge", onClose: closeFareCompare, scroll: "body", keepMounted: false },
            React.createElement(CompareFares, { orderId: props.orderId }))));
};
export default DesktopFlightModalContent;
